import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import dashImg from "../../assets/images/dash.png";
import phoneImg from '../../assets/images/phone.png';
import styles from './Hero.module.css';

// Responsive hook
const useResponsive = () => {
  const [width, setWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return {
    isMobile: width <= 760,
    isTablet: width > 760 && width <= 1024,
  };
};

// Animation variants
const textContainerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.18, delayChildren: 0.18 } },
};
const textItemVariants = {
  hidden: { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

// TypingWords: escribe letra por letra y cicla palabras
function TypingWords({ words = [], colors = [], typingSpeed = 80, deletingSpeed = 40, pause = 900 }) {
  const [display, setDisplay] = React.useState('');
  const [wordIndex, setWordIndex] = React.useState(0);
  const [charIndex, setCharIndex] = React.useState(0);
  const [deleting, setDeleting] = React.useState(false);

  React.useEffect(() => {
    if (!words || words.length === 0) return;

    let timeout;
    const currentWord = words[wordIndex % words.length];

    if (!deleting) {
      // typing
      if (charIndex <= currentWord.length) {
        timeout = setTimeout(() => {
          setDisplay(currentWord.slice(0, charIndex));
          setCharIndex((c) => c + 1);
        }, typingSpeed);
      } else {
        // word complete, pause then start deleting
        timeout = setTimeout(() => setDeleting(true), pause);
      }
    } else {
      // deleting
      if (charIndex >= 0) {
        timeout = setTimeout(() => {
          setDisplay(currentWord.slice(0, charIndex));
          setCharIndex((c) => c - 1);
        }, deletingSpeed);
      } else {
        // move to next word
        setDeleting(false);
        setWordIndex((i) => (i + 1) % words.length);
        setCharIndex(0);
      }
    }

    return () => clearTimeout(timeout);
  }, [charIndex, deleting, wordIndex, words, typingSpeed, deletingSpeed, pause]);

  const color = colors && colors.length > 0 ? colors[wordIndex % colors.length] : 'inherit';

  // calcular longitud máxima de las palabras para reservar espacio y evitar reflow en mobile
  const maxLen = words && words.length ? Math.max(...words.map(w => (w || '').length)) : 0;

  return (
    <span
      className={styles ? styles.typingWord : undefined}
      style={{ display: 'inline-block', color, minWidth: `${maxLen}ch` }}
    >
      {display}
    </span>
  );
}

const heroTitle = (
  <>
    Cobrar nunca
    <br />
    fue tan <TypingWords words={["sencillo.", "rápido.", "seguro."]} colors={["#000000","#ff4d00","#28a745"]} />
  </>
);

const heroSubtitle = (
  <>
    Con <span className={styles.r4}>R4</span> <span className={styles.plink}>Plink</span>, cobra a tus clientes al instante usando enlaces de cobro, sin complicaciones y de forma segura.
  </>
);

function Hero() {
  const { isMobile, isTablet } = useResponsive();

  if (isMobile) {
    return (
      <section
        className=""
        id="hero"
        style={{
          width: '100%',
          paddingTop: 110,
          paddingBottom: 40,
          boxSizing: 'border-box',
          background: '#ffffff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 20,
        }}
      >
        <motion.div
          variants={textContainerVariants}
          initial="hidden"
          animate="visible"
          style={{
            width: '100%',
            maxWidth: 420,
            padding: '0 20px',
            boxSizing: 'border-box',
            textAlign: 'center',
            marginTop: 0,
          }}
        >
          <motion.h1
            variants={textItemVariants}
            className={styles.heroMobileTitle}
            style={{
              margin: 0,
              fontFamily: 'Poppins, Arial, sans-serif',
              fontWeight: 600,
              color: '#000',
              lineHeight: 1.03,
              letterSpacing: '-0.02em',
            }}
          >
            Cobrar 
            <br />
            nunca fue 
            tan <TypingWords words={["sencillo.", "rápido.", "seguro."]} colors={["#000000","#ff4d00","#28a745"]} />
          </motion.h1>

          <motion.p
            variants={textItemVariants}
            style={{
              marginTop: 50,
              marginBottom: 10,
              width: '100%',
              maxWidth: 410,
              marginLeft: 'auto',
              marginRight: 'auto',
              position: 'relative',
              fontFamily: 'Poppins, Arial, sans-serif',
              fontSize: 'clamp(18px, 5vw, 26px)',
              lineHeight: '145%',
              textAlign: 'center',
              color: 'rgba(0, 0, 0, 0.55)',
              fontWeight: 400,
            }}
          >
            {heroSubtitle}
          </motion.p>
        </motion.div>

        <motion.img
          src={dashImg}
          alt="Dashboard"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: 'easeOut' }}
          style={{
            width: '102%',
            maxWidth: 940,
            height: 'auto',
            objectFit: 'contain',
            background: 'transparent',
            boxShadow: '0 18px 40px rgba(255, 255, 255, 0)',
            transform: 'rotate(1deg)',
            borderRadius: 12,
            marginLeft: 290,
          }}
        />

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.25, ease: 'easeOut' }}
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-start',
            marginTop: -290,
            marginRight: 160,
            pointerEvents: 'auto',
          }}
        >
          <img
            src={phoneImg}
            alt="Phone"
            className={styles.phoneMobileControl}
          />
        </motion.div>

        {/* Párrafo móvil centrado al final */}
        <div
          style={{
            width: '100%',
            padding: '56px min(8vw, 40px) 72px', // espacio lateral uniforme controlado por viewport
            boxSizing: 'border-box',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <p
            name="plink_description"
            className={styles.gradientTextAnimated}
            style={{
              margin: 0,
              maxWidth: 620,
              fontFamily: 'Poppins, Arial, sans-serif',
              fontSize: 'clamp(15px, 4vw, 19px)',
              lineHeight: '168%',
              textAlign: 'center',
              padding: '0 min(8vw, 40px)', // más separación lateral en móvil
            }}
          >
            Es una solución de cobros digitales basada en links de cobro y códigos QR, diseñada para facilitar cobros de forma rápida y sin fricciones mediante Débito Inmediato en bancos nacionales. Permite a cualquier comercio o profesional compartir un enlace de cobro personalizado por canales digitales como WhatsApp o Email.
          </p>
        </div>
      </section>
    );
  }

  // Desktop / Tablet
  return (
    <div
      className=""
      style={{
        width: '120vw',
        minHeight: isTablet ? '60vh' : '110vh',
        position: 'relative',
        overflow: 'hidden',
        padding: isTablet ? '560px 0 16px' : '80px 0 48px',
        marginTop: isTablet ? '9vh' : '3vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        background: 'transparent',
      }}
    >
      <motion.div
        className={styles.textColumn}
        style={{
          width: isTablet ? '50vw' : '42vw',
          minWidth: 320,
          maxWidth: isTablet ? 900 : 800,
          position: 'absolute',
          left: isTablet ? '20vw' : '21vw',
          top: isTablet ? '11vh' : '24vh',
          padding: '10px 0',
          display: 'flex',
          flexDirection: 'column',
          gap: 24,
          zIndex: 2,
          alignItems: 'flex-start',
          textAlign: 'left',
        }}
        variants={textContainerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.h1
          style={{
            color: 'black',
            fontFamily: 'Poppins, Arial, sans-serif',
            fontSize: isTablet ? 'clamp(56px, 8vw, 120px)' : 'clamp(40px, 4.2vw, 96px)',
            lineHeight: 1.05,
            fontWeight: 700,
            margin: 0,
          }}
          variants={textItemVariants}
        >
          {isTablet ? (
            <>Cobrar nunca fue
            <br />
            tan <TypingWords words={["sencillo.", "rápido.", "seguro."]} colors={["#000000","#ff7a00","#28a745"]} /></>
          ) : (
            heroTitle
          )}
        </motion.h1>
        <motion.p
          style={{
            color: 'rgba(0, 0, 0, 1)',
            fontSize: 'clamp(20px, 1.8vw, 30px)',
            width: '100%',
            maxWidth: '820px',
            position: 'relative',
            fontFamily: 'Poppins, Arial, sans-serif',
            fontWeight: 400,
            lineHeight: '145%',
            textAlign: 'left',
            margin: 0
          }}
          variants={textItemVariants}
        >
          {heroSubtitle}
        </motion.p>
      </motion.div>

      <motion.img
        src={dashImg}
        alt="Dashboard"
        initial={{ opacity: 0, x: 120 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 1, ease: 'easeOut' }}
        style={{
          width: isTablet ? '84vw' : '80vw',
          maxWidth: 1800,
          height: 'auto',
          position: 'absolute',
          left: '58%',
          top: '100px',
          transform: 'translateX(-50%) rotate(4deg)',
          zIndex: 1,
          mixBlendMode: 'multiply',
          objectFit: 'contain',
        }}
      />

      <motion.div
        style={{
          position: 'absolute',
          left: '60vw',
          top: isTablet ? 'calc(160px + 6vh)' : 'calc(260px + 10vh)',
          zIndex: 3,
          transform: 'translateX(-50%)',
        }}
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: 'easeOut', delay: 0.5 }}
      >
        <img
            style={{
              width: isTablet ? '38vw' : '34vw',
              maxWidth: 700,
              height: 'auto',
              display: 'block',
            transform: 'rotate(1deg)',
          }}
          src={phoneImg}
          alt="Phone"
          className={
            isTablet
              ? styles.phoneTabletControl
              : styles.phoneImage
          }
        />
      </motion.div>

      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          boxSizing: 'border-box',
          padding: isTablet ? '40px 60px 50px' : '80px 120px 50px', // más espacio lateral e inferior en desktop
          marginTop: isTablet ? 'calc(50px)' : 'calc(460px + 12vh)', // empujar más abajo en desktop
        }}
      >
        <p
          name="plink_description"
          className={styles.gradientTextAnimated}
        >
          Es una solución de cobros digitales basada en links de cobro y códigos QR, diseñada para facilitar cobros de forma rápida y sin
          fricciones mediante Débito Inmediato en bancos nacionales. Permite a cualquier comercio o profesional compartir un enlace de
          cobro personalizado por canales digitales como WhatsApp o Email.
        </p>
      </div>
  </div>


  );
}

export default Hero;
